import { useDroppable } from '@dnd-kit/react'
import type { TestGroupingGroup, TestGroupingItem } from '@vedalib/editor/lib/elements'
import { AnimatePresence } from 'framer-motion'
import { useState, useEffect, useRef } from 'react'
import { useContext } from 'use-context-selector'

import TestGroupingContext from '../../TestGroupingContext'
import * as s from './GroupCard.module.scss'
import ScaledAnswer from './ScaledAnswer'
import Stack from './Stack'

interface IGroupCardProps {
  group: TestGroupingGroup
}

const GroupCard = ({ group }: IGroupCardProps) => {
  const { destination, styles, blockResult } = useContext(TestGroupingContext)
  const { ref, isDropTarget } = useDroppable({
    id: group.value,
    data: group,
    disabled: !!blockResult,
  })
  const [animatingItem, setAnimatingItem] = useState<TestGroupingItem | null>(null)
  const previousItemsRef = useRef<TestGroupingItem[]>(group.items || [])

  useEffect(() => {
    const currentItems = group.items || []
    const previousItems = previousItemsRef.current

    const newItem = currentItems.find(
      (item) => !previousItems.some((prevItem) => prevItem.value === item.value),
    )

    if (newItem) {
      setAnimatingItem(newItem)
      setTimeout(() => {
        setAnimatingItem(null)
      }, 400)
    }

    previousItemsRef.current = currentItems
  }, [group.items, group.value, destination])

  const stackItems = animatingItem
    ? (group.items || []).filter((item) => item.value !== animatingItem.value)
    : group.items || []

  return (
    <div className={s.groupWrapper}>
      <div
        className={s.droppableContainer}
        ref={ref as React.RefCallback<HTMLDivElement>}
        style={{
          ...styles.common,
          outline: isDropTarget ? '3px solid currentColor' : undefined,
        }}
        tabIndex={-1}
      >
        <AnimatePresence mode='wait'>
          {animatingItem && <ScaledAnswer item={animatingItem} key={animatingItem.value} />}
        </AnimatePresence>
      </div>
      <div className={s.stackContainer} style={styles.common}>
        <Stack groupValue={group.value} items={stackItems} />
      </div>
    </div>
  )
}

export default GroupCard
